import { useEffect, useRef, useState } from "react";

import { useGetRestaurantQuery } from "../../hooks/useGetRestaurantQuery";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import { convertCentsToDollars, getQuantityRemainingText, isProductQuantityAvailable } from "../../util/util";
import { ProductModal } from "../modals/product";
import { SearchProductModal } from "../modals/searchProductModal";
import { IGET_RESTAURANT_PRODUCT, IGET_RESTAURANT_CATEGORY, IS3Object } from "../../graphql/customQueries";
import { useCart } from "../../context/cart-context";
import { PageWrapper } from "../../tabin/components/pageWrapper";
// import { ItemAddedUpdatedModal } from "../modals/itemAddedUpdatedModal";
import { ICartProduct } from "../../model/model";
import { isItemAvailable, isItemSoldOut } from "../../util/util";
import { getCloudFrontDomainName, getPublicCloudFrontDomainName } from "../../private/aws-custom";
//@ts-ignore as it does not have the types
import { Shake } from "reshake";
import { useRestaurant } from "../../context/restaurant-context";

import "./restaurant.scss";
import { CachedImage } from "../../tabin/components/cachedImage";
import { useRegister } from "../../context/register-context";
import { Checkout } from "./checkout";
import { isMobile } from "react-device-detect";
import { Button } from "../../tabin/components/button";
import { checkoutPath } from "../main";
import { useLocation, useNavigate, useParams } from "react-router";
import { useUser } from "../../context/user-context";
import { isToday, parseISO } from "date-fns";

interface IMostPopularProduct {
    category: IGET_RESTAURANT_CATEGORY;
    product: IGET_RESTAURANT_PRODUCT;
}

export const Restaurant = () => {
    const location = useLocation();

    const { restaurantId, selectedCategoryId, selectedProductId } = useParams();

    const queryParams = new URLSearchParams(location.search);
    const table = queryParams.get("table");

    const { user } = useUser();
    const { setTableNumber } = useCart();

    setTableNumber(table);

    // context
    const { cartProductQuantitiesById, addProduct, setProducts, products, subTotal } = useCart();
    const { register, selectRegister } = useRegister();
    const { setRestaurant, menuCategories: restaurantCategories, menuProducts: restaurantProducts } = useRestaurant();

    const navigate = useNavigate();

    // query
    const { data: restaurant, error: getRestaurantError, loading: getRestaurantLoading } = useGetRestaurantQuery(restaurantId || "");

    // states
    const [selectedCategory, setSelectedCategory] = useState<IGET_RESTAURANT_CATEGORY | null>(null);
    //selectedCategoryForProductModal is a different one so that when you either search for a product or select a most popular product it does not change the selectedCategory.
    const [selectedCategoryForProductModal, setSelectedCategoryForProductModal] = useState<IGET_RESTAURANT_CATEGORY | null>(null);
    const [selectedProductForProductModal, setSelectedProductForProductModal] = useState<IGET_RESTAURANT_PRODUCT | null>(null);
    const [showProductModal, setShowProductModal] = useState(false);
    // const [showItemAddedModal, setShowItemAddedModal] = useState(false);
    const [showSearchProductModal, setShowSearchProductModal] = useState(false);

    const [mostPopularProducts, setMostPopularProducts] = useState<IMostPopularProduct[]>([]);
    const [subCategories, setSubCategories] = useState<string[]>([]);

    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>();

    const [isShakeAnimationActive, setIsShakeAnimationActive] = useState(false);
    const startShakeAfterSeconds = 30;
    const shakeButtonDurationSeconds = 5;
    const userOnPageDuration: React.MutableRefObject<number> = useRef(1);

    useEffect(() => {
        const ticker = setInterval(() => {
            if (userOnPageDuration.current % startShakeAfterSeconds == 0) {
                setIsShakeAnimationActive(true);
            }

            if (userOnPageDuration.current % startShakeAfterSeconds == shakeButtonDurationSeconds) {
                setIsShakeAnimationActive(false);
            }

            userOnPageDuration.current++;
        }, 1000);

        return () => clearTimeout(ticker);
    }, []);

    useEffect(() => {
        if (restaurant) {
            setRestaurant(restaurant);

            restaurant.registers.items.forEach((r) => {
                if (r.type == "ONLINE") {
                    selectRegister(r);

                    if (selectedCategoryId) {
                        const selectedCategoryItem = restaurantCategories[selectedCategoryId];

                        if (selectedCategoryItem) setSelectedCategory(selectedCategoryItem);
                    } else if (register && register.defaultCategoryView) {
                        const selectedCategoryItem = restaurantCategories[register.defaultCategoryView];

                        if (selectedCategoryItem) setSelectedCategory(selectedCategoryItem);
                    }

                    const cartProducts: ICartProduct[] = [];

                    if (sessionStorage.getItem("haveNotRedeemedFreeFries") === "true") {
                        Object.values(restaurantProducts).forEach((freeFriesProduct) => {
                            if (freeFriesProduct.name.toLowerCase() === "free fries") {
                                const freeFriesProductToOrder: ICartProduct = {
                                    isPreSelectedProduct: true,
                                    id: freeFriesProduct.id,
                                    name: freeFriesProduct.name,
                                    kitchenName: freeFriesProduct.kitchenName,
                                    price: freeFriesProduct.price,
                                    totalPrice: freeFriesProduct.price,
                                    discount: 0,
                                    image: freeFriesProduct.image
                                        ? {
                                              key: freeFriesProduct.image.key,
                                              region: freeFriesProduct.image.region,
                                              bucket: freeFriesProduct.image.bucket,
                                              identityPoolId: freeFriesProduct.image.identityPoolId,
                                          }
                                        : null,
                                    quantity: 1,
                                    notes: null,
                                    category: {
                                        id: freeFriesProduct.categories.items[0].category.id,
                                        name: freeFriesProduct.categories.items[0].category.name,
                                        kitchenName: freeFriesProduct.categories.items[0].category.kitchenName,
                                        image: freeFriesProduct.categories.items[0].category.image
                                            ? {
                                                  key: freeFriesProduct.categories.items[0].category.image.key,
                                                  region: freeFriesProduct.categories.items[0].category.image.region,
                                                  bucket: freeFriesProduct.categories.items[0].category.image.bucket,
                                                  identityPoolId: freeFriesProduct.categories.items[0].category.image.identityPoolId,
                                              }
                                            : null,
                                    },
                                    modifierGroups: [],
                                };

                                cartProducts.push(freeFriesProductToOrder);
                            }
                        });
                    } else {
                        if (!user) return;

                        const date = parseISO(user.createdAt);

                        if (isToday(date) && sessionStorage.getItem("haveNotRedeemedFreeFries") !== "false") {
                            sessionStorage.setItem("haveNotRedeemedFreeFries", "true");
                        }
                    }

                    if (restaurantProducts && r.preSelectedProducts) {
                        r.preSelectedProducts.forEach((preSelectedProduct) => {
                            const product = restaurantProducts[preSelectedProduct];

                            if (product) {
                                const productToOrder: ICartProduct = {
                                    isPreSelectedProduct: true,
                                    id: product.id,
                                    name: product.name,
                                    kitchenName: product.kitchenName,
                                    price: product.price,
                                    totalPrice: product.price,
                                    discount: 0,
                                    image: product.image
                                        ? {
                                              key: product.image.key,
                                              region: product.image.region,
                                              bucket: product.image.bucket,
                                              identityPoolId: product.image.identityPoolId,
                                          }
                                        : null,
                                    quantity: 1,
                                    notes: null,
                                    category: {
                                        id: product.categories.items[0].category.id,
                                        name: product.categories.items[0].category.name,
                                        kitchenName: product.categories.items[0].category.kitchenName,
                                        image: product.categories.items[0].category.image
                                            ? {
                                                  key: product.categories.items[0].category.image.key,
                                                  region: product.categories.items[0].category.image.region,
                                                  bucket: product.categories.items[0].category.image.bucket,
                                                  identityPoolId: product.categories.items[0].category.image.identityPoolId,
                                              }
                                            : null,
                                    },
                                    modifierGroups: [],
                                };

                                cartProducts.push(productToOrder);
                            }
                        });
                    }

                    if (cartProducts && cartProducts.length > 0) setProducts(cartProducts);

                    // if (selectedProductId) {
                    //     const selectedProductItem = restaurantProducts[selectedProductId];

                    //     if (selectedProductItem) setSelectedProductForProductModal(selectedProductItem);

                    //     setShowProductModal(true);
                    // }
                }
            });
        }
    }, [restaurant, restaurantCategories, restaurantProducts]);

    const onProcessSubCategories = (category: IGET_RESTAURANT_CATEGORY) => {
        const newSubCategories: string[] = [];

        category.products &&
            category.products.items.forEach((p) => {
                p.product.subCategories &&
                    p.product.subCategories.split(";").forEach((subCategory) => {
                        if (!newSubCategories.includes(subCategory)) newSubCategories.push(subCategory);
                    });
            });

        setSubCategories(newSubCategories);

        if (newSubCategories.length > 0) {
            setSelectedSubCategory(newSubCategories[0]);
        } else {
            setSelectedSubCategory(null);
        }
    };

    // useEffect(() => {
    //     if (restaurant) {
    //         setRestaurant(restaurant);

    //         // restaurant.categories.items.every((c) => {
    //         //     if (isItemAvailable(c.availability)) {
    //         //         setSelectedCategory(c);
    //         //         return false;
    //         //     }
    //         //     return true;
    //         // });
    //     }
    // }, [restaurant]);

    const compareSortFunc = (a: IMostPopularProduct, b: IMostPopularProduct) => {
        if (!a.product.totalQuantitySold || !b.product.totalQuantitySold) return 0;
        if (a.product.totalQuantitySold > b.product.totalQuantitySold) return -1;
        if (a.product.totalQuantitySold < b.product.totalQuantitySold) return 1;
        return 0;
    };

    useEffect(() => {
        if (!restaurant || !register) return;

        const newMostPopularProducts: IMostPopularProduct[] = [];

        restaurant.categories.items.forEach((c) => {
            if (c.name !== "COUPONS") {
                if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
            }

            c.products &&
                c.products.items.forEach((p) => {
                    if (c.name !== "COUPONS") {
                        if (p.product.availablePlatforms && !p.product.availablePlatforms.includes(register.type)) return;
                    }

                    if (p.product.totalQuantitySold) {
                        newMostPopularProducts.push({
                            category: c,
                            product: p.product,
                        });
                    }
                });
        });

        newMostPopularProducts.sort(compareSortFunc);
        setMostPopularProducts(newMostPopularProducts.slice(0, 20));
    }, [restaurant, register]);

    useEffect(() => {
        if (showProductModal) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [showProductModal]);

    // displays THAT SHOULD BE IN CONTEXT
    if (getRestaurantLoading) {
        return <FullScreenSpinner show={true} text="Loading restaurant" />;
    }

    if (getRestaurantError) {
        return <h1>Couldn't get restaurant. Try Refreshing, {getRestaurantError.message}</h1>;
    }

    if (!restaurant) {
        return <div>Restaurant does not exist</div>;
    }

    if (!restaurant.verified) {
        return <div>Restaurant is not verified</div>;
    }

    if (!register) {
        return <div>This restaurant has not been set up for online ordering!</div>;
    }

    // callbacks
    const onCloseProductModal = () => {
        setShowProductModal(false);
    };

    const onAddProduct = (product: ICartProduct) => {
        addProduct(product);
        // setShowItemAddedModal(true);
    };

    // const onCloseItemAddedModal = () => {
    //     setShowItemAddedModal(false);
    // };

    const onCloseSearchProductModal = () => {
        setShowSearchProductModal(false);
    };

    const onClickProduct = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) => {
        setSelectedCategoryForProductModal(category);
        setSelectedProductForProductModal(product);
        setShowProductModal(true);
    };

    const onClickSearchProduct = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) => {
        setSelectedCategoryForProductModal(category);
        setSelectedProductForProductModal(product);
        onCloseSearchProductModal();
        setShowProductModal(true);
    };

    const productModal = (
        <>
            {selectedCategoryForProductModal && selectedProductForProductModal && showProductModal && (
                <ProductModal
                    isOpen={showProductModal}
                    category={selectedCategoryForProductModal}
                    product={selectedProductForProductModal}
                    onAddProduct={onAddProduct}
                    onClose={onCloseProductModal}
                />
            )}
        </>
    );

    // const itemAddedModal = (
    //     <>{showItemAddedModal && <ItemAddedUpdatedModal isOpen={showItemAddedModal} onClose={onCloseItemAddedModal} isProductUpdate={false} />}</>
    // );

    const searchProductModal = (
        <>
            {showSearchProductModal && (
                <SearchProductModal isOpen={showSearchProductModal} onClose={onCloseSearchProductModal} onClickSearchProduct={onClickSearchProduct} />
            )}
        </>
    );

    const modals = (
        <>
            {productModal}
            {/* {itemAddedModal} */}
            {searchProductModal}
        </>
    );

    const ProductDisplay = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) => {
        const isSoldOut = isItemSoldOut(product.soldOut, product.soldOutDate);
        const isProductAvailable = isItemAvailable(product.availability);
        const isCategoryAvailable = isItemAvailable(category.availability);
        const isQuantityAvailable = isProductQuantityAvailable(product, cartProductQuantitiesById);

        const isValid = !isSoldOut && isProductAvailable && isCategoryAvailable && isQuantityAvailable;

        const addToCart = products && products.find((item) => item.id === product.id);

        return (
            <>
                <div
                    key={product.id}
                    className={`product ${isValid ? "" : "sold-out"} ${addToCart ? "add-to-cart" : ""}`}
                    onClick={() => isValid && onClickProduct(category, product)}
                >
                    <div className="product-quantity">{addToCart && addToCart.quantity}</div>
                    {product.totalQuantityAvailable && product.totalQuantityAvailable <= 5 ? (
                        <span className="quantity-remaining ml-2">{getQuantityRemainingText(product.totalQuantityAvailable)}</span>
                    ) : (
                        <></>
                    )}

                    {product.imageUrl ? (
                        <CachedImage url={`${product.imageUrl}`} className="image mb-2" alt="product-image" />
                    ) : product.image ? (
                        <>
                            <CachedImage
                                className="image mb-2"
                                url={`${getCloudFrontDomainName()}/protected/${product.image.identityPoolId}/${product.image.key}`}
                                alt="product-image"
                            />
                        </>
                    ) : null}

                    <div className="name text-bold">{isValid ? `${product.name}` : `${product.name} (SOLD OUT)`}</div>

                    {product.description && <div className="description mt-2">{product.description}</div>}

                    {product.tags && (
                        <div className="tags mt-2">
                            {product.tags.split(";").map((tag) => (
                                <div className="tag">{tag}</div>
                            ))}
                        </div>
                    )}

                    <div className={`display-price mt-4 ${product.displayPrice ? "" : "display-none"}`}>{product.displayPrice}</div>
                    <div className={`price mt-4 ${product.displayPrice ? "display-none" : ""}`}>${convertCentsToDollars(product.price)}</div>
                </div>
            </>
        );
    };

    const Category = (props: {
        isSelected: boolean;
        category: IGET_RESTAURANT_CATEGORY;
        onCategorySelected: (category: IGET_RESTAURANT_CATEGORY) => void;
    }) => {
        const { isSelected, category, onCategorySelected } = props;

        const isSoldOut = isItemSoldOut(category.soldOut, category.soldOutDate);
        const isAvailable = isItemAvailable(category.availability);

        const isValid = !isSoldOut && isAvailable;

        return (
            <div
                key={category.id}
                className={`category ${isSelected ? "selected" : ""} ${isValid ? "" : "sold-out"}`}
                onClick={() => {
                    isValid && onCategorySelected(category);
                }}
            >
                {!isValid ? (
                    <div className={`name`}>{category.name} (UNAVAILABLE)</div>
                ) : isSelected ? (
                    <div className="name text-bold">{category.name}</div>
                ) : (
                    <div className="name">{category.name}</div>
                )}
            </div>
        );
    };

    const menuCategories = (
        <>
            {restaurant.categories.items.map((c, index) => {
                if (c.name !== "COUPONS") {
                    if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
                }

                return (
                    <Category
                        key={c.id}
                        isSelected={selectedCategory != null && selectedCategory.id == c.id}
                        category={c}
                        onCategorySelected={(category: IGET_RESTAURANT_CATEGORY) => {
                            setSelectedCategory(category);
                            onProcessSubCategories(category);
                        }}
                    />
                );
            })}
        </>
    );

    const menuSearchProduct = (
        <>
            <Shake active={isShakeAnimationActive} h={5} v={5} r={3} dur={300} int={10} max={100} fixed={true} fixedStop={false} freez={false}>
                <div
                    className="category background-grey"
                    onClick={() => {
                        setShowSearchProductModal(true);
                    }}
                >
                    <CachedImage className="icon" url={`${getPublicCloudFrontDomainName()}/images/search-icon.png`} alt="search-icon" />
                    <div className="name">Search</div>
                </div>
            </Shake>
        </>
    );

    const menuMostPopularCategory = (
        <div
            className={`category background-grey ${!selectedCategory ? "selected" : ""}`}
            onClick={() => {
                setSelectedCategory(null);
            }}
        >
            {/* <CachedImage className="icon" url={`${getPublicCloudFrontDomainName()}/images/most-popular.png`} alt="most-popular-icon" /> */}
            <div className="name">Most Popular</div>
        </div>
    );

    const menuMostPopularProducts = (
        <div>
            {!selectedCategory && (
                <>
                    <div className="product-category-name h1 text-center">Most Popular</div>
                    <div className="products">
                        {mostPopularProducts.map((mostPopularProduct) => ProductDisplay(mostPopularProduct.category, mostPopularProduct.product))}
                    </div>
                </>
            )}
        </div>
    );

    const menuProducts = (
        <div>
            {selectedCategory &&
                restaurant.categories.items.map((c) => {
                    if (selectedCategory.id !== c.id) return;
                    if (c.name !== "COUPONS") {
                        if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
                    }

                    return (
                        <>
                            <div className="product-category-name h1 text-center mb-6">{c.name}</div>
                            {c.description && <div className="product-category-description text-bold text-center mb-6 h3">{c.description}</div>}
                            {subCategories.length > 0 && (
                                <div className="product-sub-category-wrapper mb-6">
                                    {subCategories.map((subCategory) => (
                                        <div
                                            className={`product-sub-category h3 ${selectedSubCategory === subCategory ? "selected" : ""}`}
                                            onClick={() => setSelectedSubCategory(subCategory)}
                                        >
                                            {subCategory}
                                        </div>
                                    ))}
                                    <div
                                        className={`product-sub-category background-grey h3 ${selectedSubCategory === null ? "selected" : ""}`}
                                        onClick={() => setSelectedSubCategory(null)}
                                    >
                                        All
                                    </div>
                                </div>
                            )}
                            {/* {c.image && (
                                <div className="product-category-image-wrapper mb-6">
                                    <CachedImage
                                        url={`${getCloudFrontDomainName()}/protected/${c.image.identityPoolId}/${c.image.key}`}
                                        className="product-category-image"
                                        alt="category-image"
                                    />
                                </div>
                            )} */}
                            <div className="products">
                                {c.products &&
                                    c.products.items.map((p) => {
                                        if (c.name !== "COUPONS") {
                                            if (p.product.availablePlatforms && !p.product.availablePlatforms.includes(register.type)) return;
                                        }
                                        if (
                                            (selectedSubCategory &&
                                                p.product.subCategories &&
                                                !p.product.subCategories.split(";").includes(selectedSubCategory)) ||
                                            (selectedSubCategory && selectedSubCategory && !p.product.subCategories)
                                        )
                                            return;

                                        return ProductDisplay(c, p.product);
                                    })}
                            </div>
                        </>
                    );
                })}
        </div>
    );

    const checkout = (
        <>
            <div className="checkout">
                <Checkout />
            </div>
        </>
    );

    const onClickCart = () => {
        navigate(checkoutPath);
    };

    const restaurantFooter = (
        <>
            <div className="total-container">
                <div className="total-wrapper">
                    {/* <CachedImage
                        className="shopping-bag-icon mr-2"
                        url={`${getPublicCloudFrontDomainName()}/images/shopping-bag-icon.png`}
                        alt="shopping-bag-icon"
                    /> */}
                    <div className="h4 total">Total: ${convertCentsToDollars(subTotal)}</div>
                </div>
                <Button className="view-my-order-button" disabled={!products || products.length == 0} onClick={onClickCart}>
                    View My Order
                </Button>
            </div>
            {/* <Button className="cancel-button" onClick={onCancelOrder}>
                Cancel Order
            </Button> */}
        </>
    );

    const RestaurantLogo = (props: { image: IS3Object }) => {
        return (
            <div className="restaurant-logo-wrapper">
                <CachedImage
                    url={`${getCloudFrontDomainName()}/protected/${props.image.identityPoolId}/${props.image.key}`}
                    className="restaurant-logo"
                    alt="restaurant-logo"
                />
            </div>
        );
    };

    return (
        <>
            <PageWrapper>
                <div className="restaurant">
                    <div className="restaurant-container">
                        {restaurant && restaurant.logo && <RestaurantLogo image={restaurant.logo} />}
                        <div className="categories-wrapper">
                            {/* {restaurant.logo && <RestaurantLogo image={restaurant.logo} />} */}
                            {/* {menuSearchProduct} */}
                            {menuMostPopularCategory}
                            {menuCategories}
                        </div>
                        <div className="products-wrapper">
                            {menuMostPopularProducts}
                            {menuProducts}
                        </div>
                    </div>
                    {isMobile && products && products.length > 0 && <div className="footer-wrapper">{restaurantFooter}</div>}

                    {!isMobile && <div className="checkout-container">{checkout}</div>}
                </div>
                {modals}
            </PageWrapper>
        </>
    );
};

const RestaurantLogo = (props: { image: IS3Object }) => {
    return (
        <CachedImage
            url={`${getCloudFrontDomainName()}/protected/${props.image.identityPoolId}/${props.image.key}`}
            className="restaurant-logo"
            alt="restaurant-logo"
        />
    );
};
